import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { calculatePriceWithVAT } from "@/lib/utils";
import { Eye, Trash2, PenLine, Info, Archive, ArchiveRestore } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import {
  DeleteConfirmDialog,
  AddOns,
  DetailsDialog,
  AllergenDialog,
  VariantDialog,
  ArchiveConfirmDialog,
} from "@/components/dialog/common-dialog";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
  deleteFishChipsById,
  clearFishChipsMessages,
  updateExistingFishChips,
  fetchAllFishChips,
} from "@/redux/slice/menu-manage-slice/fish-chips";
import hasPermission from "@/hooks/hasPermission";
import { Switch } from "@/components/ui/switch";
import { toggleItemStatus } from "@/api/new-api/staff-management/menu-manage/toggle-status";
import useAuth from "@/hooks/useAuth";

function FishListTable({ data }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { deleteLoading, updateLoading, requestStatus, error } = useSelector(
    (state) => state.menuManage.fishChips
  );
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [archiveActionType, setArchiveActionType] = useState("archive");
  const [priceDialog, setPriceDialog] = useState(false);
  const [addOnsDialog, setAddOnsDialog] = useState(false);
  const [allergenDialog, setAllergenDialog] = useState(false);
  const [fishAndChipsDialog, setFishAndChipsDialog] = useState(false);
  const [itemsDialog, setItemsDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const closeArchiveDialog = () => {
    setArchiveDialog(false);
    setItemsDialog(null);
  };
  const closeFishChipsDialog = () => {
    setFishChipsDialog(false);
    setItemsDialog(null);
  };

  const closeAddOns = () => {
    setAddOnsDialog(false);
    setItemsDialog(null);
  };

  const colseFishAndChips = () => {
    setFishAndChipsDialog(false);
    setItemsDialog(null);
  };

  const archiveHandler = async (id, item, type) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearFishChipsMessages());
      const newStatus = type === "archive" ? "archived" : "active";
      const res = await dispatch(
        updateExistingFishChips({
          id,
          data: {
            ...item,
            status: newStatus,
          },
        })
      );
      if (updateExistingFishChips.fulfilled.match(res)) {
        toast.success(`Fish & Chips archived successfully!`);
      } else if (updateExistingFishChips.rejected.match(res)) {
        toast.error(res?.payload || `Failed to archive fish & chips.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      closeArchiveDialog();
    }
  };

  const deleteHandler = async (id) => {
    try {
      if (!id) {
        toast.error("ID is required.");
        return;
      }
      dispatch(clearFishChipsMessages());
      const res = await dispatch(deleteFishChipsById(id));
      if (deleteFishChipsById.fulfilled.match(res)) {
        toast.success(`Fish & Chips deleted successfully!`);
      } else if (deleteFishChipsById.rejected.match(res)) {
        toast.error(res?.payload || `Failed to delete fish & chips.`);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    } finally {
      setDeleteDialog(false);
      setItemsDialog(null);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await toggleItemStatus("fish-chips", id);
      if (res.status === "success") {
        toast.success(res.message || "Fish & Chips status updated successfully");
        dispatch(fetchAllFishChips(user?.restaurant_id));
      } else {
        toast.error(res.message || "Failed to toggle status.");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!");
    }
  };

  return (
    <Table className="w-full">
      <TableHeader>
        <TableRow>
          <TableHead className="w-[100px]">Item No</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Size</TableHead>
          <TableHead>Price</TableHead>
          <TableHead>Add-ons</TableHead>
          <TableHead>Allergen</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data && data.length > 0 ? (
          data.map((item, index) => (
            <TableRow key={index}>
              <TableCell className="font-medium">{item?.id}</TableCell>
              <TableCell>{item?.name}</TableCell>
              <TableCell>{item?.size || "N/A"}</TableCell>
              <TableCell>
                <Eye
                  onClick={() => {
                    setPriceDialog(true);
                    setItemsDialog(item);
                  }}
                  size={18}
                />
              </TableCell>
              <TableCell>
                <span className="flex gap-1 items-center cursor-pointer">
                  {item?.add_ons_count || 0}{" "}
                  <Eye
                    onClick={() => {
                      setAddOnsDialog(true);
                      setItemsDialog(item);
                    }}
                    size={18}
                  />
                </span>
              </TableCell>
              <TableCell>
                <span className="flex gap-1 items-center cursor-pointer">
                  {item?.allergens_count || 0}{" "}
                  <Eye
                    onClick={() => {
                      setAllergenDialog(true);
                      setItemsDialog(item);
                    }}
                    size={18}
                  />
                </span>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {item?.status === "active" ? (
                    <>
                      <span className="text-cgreen text-base">Active</span>
                      <Switch
                        checked={true}
                        onCheckedChange={() => handleToggleStatus(item.id)}
                      />
                    </>
                  ) : item?.status === "inactive" ? (
                    <>
                      <span className="text-cred text-base">Inactive</span>
                      <Switch
                        checked={false}
                        onCheckedChange={() => handleToggleStatus(item.id)}
                      />
                    </>
                  ) : item?.status === "archived" ? (
                    <span className="text-cred text-base">Archived</span>
                  ) : (
                    <span className="text-cred text-base">Draft</span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right justify-end flex gap-2 items-center">
                <div className="flex items-center gap-2 h-8">
                  <Info
                    onClick={() => {
                      setFishAndChipsDialog(true);
                      setItemsDialog(item);
                    }}
                    className="text-cwhite3 cursor-pointer"
                    size={18}
                  />

                  {hasPermission("back_office.menu_management.update_fish_chips") && item?.status !== "archived" && (
                    <Link
                      to={"/backoffice/menu/fish-chips/update"}
                      state={item}
                      className="cursor-pointer"
                    >
                      <PenLine className="text-cyellow" size={18} />
                    </Link>
                  )}

                  {hasPermission("back_office.menu_management.delete_fish_chips") && item?.status !== "archived" && (
                    <span>
                      {item?.status === "draft" ? (
                        <Trash2
                          onClick={() => {
                            setItemsDialog(item);
                            setDeleteDialog(true);
                          }}
                          className="text-cred cursor-pointer"
                          size={18}
                        />
                      ) : (
                        <Archive
                          onClick={() => {
                            setArchiveActionType("archive");
                            setItemsDialog(item);
                            setArchiveDialog(true);
                          }}
                          className="text-cred cursor-pointer"
                          size={18}
                        />
                      )}
                    </span>
                  )}


                </div>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={8} className="text-center">
              No data available
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      <AddOns items={itemsDialog} isOpen={addOnsDialog} onClose={closeAddOns} />
      <DetailsDialog
        items={itemsDialog}
        isOpen={fishAndChipsDialog}
        onClose={colseFishAndChips}
      />
      <AllergenDialog
        items={itemsDialog}
        isOpen={allergenDialog}
        onClose={() => {
          setAllergenDialog(false);
          setItemsDialog(null);
        }}
      />
      <PriceDialog
        items={itemsDialog}
        isOpen={priceDialog}
        onClose={() => {
          setPriceDialog(false);
          setItemsDialog(null);
        }}
      />
      <ArchiveConfirmDialog
        isLoading={updateLoading}
        isOpen={archiveDialog}
        onClose={closeArchiveDialog}
        onConfirm={() => {
          archiveHandler(itemsDialog.id, itemsDialog, archiveActionType);
        }}
        title={archiveActionType === "archive" ? "Archive Fish & Chips?" : "Restore Fish & Chips?"}
        description={
          archiveActionType === "archive"
            ? `Are you sure you want to archive "${itemsDialog?.name}"?`
            : `Are you sure you want to restore "${itemsDialog?.name}"?`
        }
        isArchive={archiveActionType === "archive"}
      />
      <DeleteConfirmDialog
        isLoading={deleteLoading}
        isOpen={deleteDialog}
        onClose={() => {
          setDeleteDialog(false);
          setItemsDialog(null);
        }}
        onConfirm={() => {
          deleteHandler(itemsDialog.id);
        }}
        title="Delete Draft Fish & Chips?"
        description={`Are you sure you want to delete the draft fish & chips "${itemsDialog?.name}"? This action cannot be undone.`}
      />
    </Table>
  );
}

export default FishListTable;

function PriceDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            Cod Fish & Chips Price
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>In-Price(exc VAT)</TableHead>
                <TableHead>In-Price(inc VAT)</TableHead>
                <TableHead>Out-Price(exc VAT)</TableHead>
                <TableHead>Out-Price(inc VAT)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>${items?.in_price || "0"}</TableCell>
                <TableCell>
                  ${calculatePriceWithVAT(items?.in_price, items?.vat) || "0"}
                </TableCell>
                <TableCell>${items?.out_price || "0"}</TableCell>
                <TableCell>
                  ${calculatePriceWithVAT(items?.out_price, items?.vat) || "0"}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
